import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { ControlledInput, ControlledInputProps } from "shared/components";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { Button, Icon, Text } from "@ttc3k/trekker";
import { Trash } from "iconoir-react";
import { useInviteStaffMember } from "./useInviteStaffMember";
import { useEffect } from "react";

export interface IInviteStaffMember {
  email: string;
  firstName: string;
  lastName: string;
}

type InviteStaffMemberProps<T extends FieldValues> = {
  entityId: string;
  form: UseFormReturn<T>;
  inputNameEmailProps: ControlledInputProps;
  inputNameFirstNameProps: ControlledInputProps;
  inputNameLastNameProps: ControlledInputProps;
  index?: number;
  onRemoveStaffToInvite?: () => void;
};

export const InviteStaffMember = <T extends FieldValues>({
  entityId,
  form,
  inputNameEmailProps,
  inputNameFirstNameProps,
  inputNameLastNameProps,
  index = 0,
  onRemoveStaffToInvite,
}: InviteStaffMemberProps<T>) => {
  const { t } = useTranslation();
  const { isDuplicateUser, namesDisabled, handleValidateEmail } =
    useInviteStaffMember<T>({
      entityId,
      form,
      index,
      inputNameEmailProps,
      inputNameFirstNameProps,
      inputNameLastNameProps,
    });

  // When the name fields are enabled, set focus to the firstName field
  // This was previously attempted in the form with a setTimeout, but
  // it didn't always work
  useEffect(() => {
    if (!namesDisabled) {
      (
        document.querySelector(
          `input[name="${inputNameFirstNameProps.name}"]`,
        ) as HTMLInputElement
      )?.focus();
    }
  }, [inputNameFirstNameProps.name, namesDisabled]);

  return (
    <Flex flexDir="column" gap="150" width="full" position="relative">
      {/* REMOVE ELEMENT */}
      {onRemoveStaffToInvite && (
        <Button
          visual="unstyled"
          borderRadius="full"
          position="absolute"
          top="12px"
          right="-26px"
          type="button"
          onClick={onRemoveStaffToInvite}
        >
          <Icon Element={Trash} size="md" color="icon.accent.cream.mid" />
        </Button>
      )}

      <ControlledInput
        placeholder={t("core:STAFF.INVITE_MODAL.LABELS.EMAIL")}
        aria-label={t("core:STAFF.INVITE_MODAL.LABELS.EMAIL")}
        flex="1"
        inputMode="email"
        {...inputNameEmailProps}
        onBlurCapture={handleValidateEmail}
        type="email"
      />
      {isDuplicateUser && (
        <Text color="text.error.dark" visual="smallMedium" marginTop="-12px">
          {t("core:STAFF.INVITE_MODAL.ERRORS.USER_EXISTS")}
        </Text>
      )}

      <Flex
        flexDir="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="150"
      >
        <ControlledInput
          placeholder={t("core:STAFF.INVITE_MODAL.LABELS.FIRST_NAME")}
          aria-label={t("core:STAFF.INVITE_MODAL.LABELS.FIRST_NAME")}
          flex="1"
          inputMode="text"
          {...inputNameFirstNameProps}
          disabled={namesDisabled}
        />
        <ControlledInput
          placeholder={t("core:STAFF.INVITE_MODAL.LABELS.LAST_NAME")}
          aria-label={t("core:STAFF.INVITE_MODAL.LABELS.LAST_NAME")}
          flex="1"
          inputMode="text"
          {...inputNameLastNameProps}
          disabled={namesDisabled}
        />
      </Flex>
    </Flex>
  );
};
